import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Fade } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const defaultPalette = {
  background: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
  color: '#ffffff',
  shadow: '0 12px 35px rgba(1, 111, 87, 0.35)',
  hoverShadow: '0 16px 42px rgba(1, 111, 87, 0.45)',
  activeTransform: 'translateY(-2px) scale(0.98)',
  hoverTransform: 'translateY(-4px) scale(1.05)',
};

const ScrollToTopButton = ({
  threshold = 500,
  hideNearBottom = true,
  hideBottomOffset = 420,
  scrollBehavior = 'smooth',
  icon,
  palette,
  size = 56,
  bottom,
  right,
  fadeProps,
  boxProps,
  ariaLabel = 'Scroll to top',
  onScrollToTop,
  sx,
}) => {
  const [visible, setVisible] = useState(false);

  const mergedPalette = useMemo(
    () => ({ ...defaultPalette, ...palette }),
    [palette]
  );

  const handleScroll = useCallback(() => {
    const pastThreshold = window.scrollY > threshold;
    if (!pastThreshold) {
      setVisible(false);
      return;
    }

    if (!hideNearBottom) {
      setVisible(true);
      return;
    }

    const doc = document.documentElement;
    const remaining = doc.scrollHeight - (window.scrollY + window.innerHeight);
    setVisible(remaining > hideBottomOffset);
  }, [threshold, hideNearBottom, hideBottomOffset]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleTrigger = (event) => {
    if (typeof onScrollToTop === 'function') {
      onScrollToTop(event);
      return;
    }

    window.scrollTo({ top: 0, behavior: scrollBehavior });
  };

  const resolvedIcon = icon ?? <ArrowUpwardIcon sx={{ fontSize: size * 0.45 }} />;

  return (
    <Fade in={visible} {...fadeProps}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleTrigger}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleTrigger(event);
          }
        }}
        sx={{
          position: 'fixed',
          bottom: bottom ?? { xs: 24, md: 32 },
          right: right ?? { xs: 24, md: 32 },
          width: size,
          height: size,
          borderRadius: '50%',
          background: mergedPalette.background,
          color: mergedPalette.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: mergedPalette.shadow,
          zIndex: 1000,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: mergedPalette.hoverTransform,
            boxShadow: mergedPalette.hoverShadow,
          },
          '&:active': {
            transform: mergedPalette.activeTransform,
          },
          '&:focus-visible': {
            outline: '2px solid rgba(1, 111, 87, 0.6)',
            outlineOffset: 4,
          },
          ...sx,
        }}
        {...boxProps}
      >
        {resolvedIcon}
      </Box>
    </Fade>
  );
};

ScrollToTopButton.propTypes = {
  threshold: PropTypes.number,
  hideNearBottom: PropTypes.bool,
  hideBottomOffset: PropTypes.number,
  scrollBehavior: PropTypes.oneOf(['auto', 'smooth']),
  icon: PropTypes.node,
  palette: PropTypes.shape({
    background: PropTypes.string,
    color: PropTypes.string,
    shadow: PropTypes.string,
    hoverShadow: PropTypes.string,
    activeTransform: PropTypes.string,
    hoverTransform: PropTypes.string,
  }),
  size: PropTypes.number,
  bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  right: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  fadeProps: PropTypes.object,
  boxProps: PropTypes.object,
  ariaLabel: PropTypes.string,
  onScrollToTop: PropTypes.func,
  sx: PropTypes.object,
};

export default ScrollToTopButton;
